import * as React from 'react';
import PropTypes from 'prop-types';
import { Tabs, Tab, Grid, Box, Typography, Paper, useMediaQuery, useTheme } from '@mui/material';
import GroupIconOutlined from '@mui/icons-material/GroupOutlined';
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import TopBar from './TopBar.jsx';
import Customer from '../customers/Customer.jsx';
import Traceability from '../traceability/Traceability.jsx';
import PanelAdmin from '../panel/PanelAdmin.jsx';
import Users from '../users/Users.jsx';
import { fetchUserAsync } from '../../slices/userSlice.js';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect } from 'react';
import { useState } from 'react';
import { fetchOfficesAsync } from '../../slices/officesSlice.js';
import { fetchCitiesAsync } from '../../slices/citySlice.js';
import { fetchDataTypeAsync } from '../../slices/dataTypeSlice.js';
import { fetchCampaignAsync } from '../../slices/campaignSlice';
import { fetchCreditsAsync } from '../../slices/creditSlice.js';
import { fetchDataTypeUserAsync } from '../../slices/typeUserSlice.js';
import { fetchUsersAsync } from '../../slices/usersDataSlice.js';
import { fetchCustomersAsync } from '../../slices/customerSlice.js';
import { fetchCustomerGroupAsync } from '../../slices/customerGroupSlice.js';
import { fetchCustomerTypeAsync } from '../../slices/customerTypeSlice.js'; 
import { fetchDisbursementOfficeAsync } from '../../slices/disbursementOfficeSlice.js'; 

const TAB_PANEL = 0;
const TAB_CUSTOMER = 1;
const TAB_TRACEABILITY = 2;
const TAB_USERS = 3;

const Dashboard = ({ children, value, index, ...other }) => (
    <div
        role="tabpanel"
        hidden={value !== index}
        id={`vertical-tabpanel-${index}`}
        aria-labelledby={`vertical-tab-${index}`}
        {...other}
    >
        {value === index && (
            <Box sx={{ p: 3 }}>
                <Typography>{children}</Typography>
            </Box>
        )}
    </div>
);

Dashboard.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

const VerticalTabs = () => {
    const [value, setValue] = useState(TAB_CUSTOMER);
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user.data);

    useEffect(() => {
        dispatch(fetchUserAsync());
        dispatch(fetchOfficesAsync());
        dispatch(fetchCitiesAsync());
        dispatch(fetchDataTypeAsync());
        dispatch(fetchCampaignAsync());
        dispatch(fetchCreditsAsync(0));
        dispatch(fetchDataTypeUserAsync());
        dispatch(fetchUsersAsync());
        dispatch(fetchCustomersAsync());
        dispatch(fetchCustomerGroupAsync());
        dispatch(fetchCustomerTypeAsync());
        dispatch(fetchDisbursementOfficeAsync());
    }, [dispatch]);

    const userType = user?.[0]?.type_user;

    const isCoordinator = userType === "Coordinador";
    const isAdmin = userType === "Administrador";

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const tabIconSize = isMobile ? 'small' : 'medium';

    const tabsSx = isMobile
        ? { width: '100%' }
        : {
            borderRight: '1px solid #e4e8f0',
            minHeight: 'calc(100vh - 80px)',
            pt: 2,
          };

    const contentSx = {
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%)',
        minHeight: isMobile ? 'calc(100vh - 120px)' : 'calc(100vh - 80px)',
        pb: isMobile ? '64px' : 0,    // espacio para la barra inferior en móvil
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            {/* TopBar ocupa siempre la fila completa */}
            <TopBar />

            {isMobile ? (
                /* ── MÓVIL: contenido arriba, tabs abajo fijos ── */
                <Box sx={{ flex: 1, position: 'relative' }}>
                    <Box sx={contentSx}>
                        {(isCoordinator || isAdmin) ? (
                            <>
                                <Dashboard value={value} index={TAB_PANEL}><PanelAdmin /></Dashboard>
                                <Dashboard value={value} index={TAB_CUSTOMER}><Customer /></Dashboard>
                                <Dashboard value={value} index={TAB_TRACEABILITY}><Traceability /></Dashboard>
                                <Dashboard value={value} index={TAB_USERS}><Users /></Dashboard>
                            </>
                        ) : (
                            <>
                                <Dashboard value={value} index={0}><Customer /></Dashboard>
                                <Dashboard value={value} index={1}><Traceability /></Dashboard>
                            </>
                        )}
                    </Box>

                    {/* Bottom Navigation fija */}
                    <Paper
                        elevation={8}
                        sx={{
                            position: 'fixed',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            zIndex: theme.zIndex.appBar,
                        }}
                    >
                        <Tabs
                            value={value}
                            onChange={handleChange}
                            orientation="horizontal"
                            variant="fullWidth"
                            sx={{ '.MuiTab-root': { minHeight: 60, fontSize: '0.65rem' } }}
                        >
                            {(isCoordinator || isAdmin) && (
                                <Tab icon={<QueryStatsIcon fontSize={tabIconSize} />} label="Panel" />
                            )}
                            <Tab icon={<GroupIconOutlined fontSize={tabIconSize} />} label="Clientes" />
                            <Tab icon={<PendingActionsOutlinedIcon fontSize={tabIconSize} />} label="Historial" />
                            {isAdmin && (
                                <Tab icon={<ManageAccountsIcon fontSize={tabIconSize} />} label="Usuarios" />
                            )}
                        </Tabs>
                    </Paper>
                </Box>
            ) : (
                /* ── DESKTOP: sidebar izquierdo, contenido derecho ── */
                <Grid container sx={{ flex: 1 }}>
                    <Grid item sx={{ width: 100, flexShrink: 0 }}>
                        <Tabs
                            value={value}
                            onChange={handleChange}
                            orientation="vertical"
                            sx={tabsSx}
                        >
                            {(isCoordinator || isAdmin) && (
                                <Tab icon={<QueryStatsIcon fontSize="large" />} label="Panel" />
                            )}
                            <Tab icon={<GroupIconOutlined fontSize="large" />} label="Clientes" />
                            <Tab icon={<PendingActionsOutlinedIcon fontSize="large" />} label="Historial" />
                            {isAdmin && (
                                <Tab sx={{ borderTop: '1px solid #e4e8f0' }} icon={<ManageAccountsIcon fontSize="large" />} label="Usuarios" />
                            )}
                        </Tabs>
                    </Grid>
                    <Grid item sx={{ flex: 1, ...contentSx }}>
                        {(isCoordinator || isAdmin) ? (
                            <>
                                <Dashboard value={value} index={TAB_PANEL}><PanelAdmin /></Dashboard>
                                <Dashboard value={value} index={TAB_CUSTOMER}><Customer /></Dashboard>
                                <Dashboard value={value} index={TAB_TRACEABILITY}><Traceability /></Dashboard>
                                <Dashboard value={value} index={TAB_USERS}><Users /></Dashboard>
                            </>
                        ) : (
                            <>
                                <Dashboard value={value} index={0}><Customer /></Dashboard>
                                <Dashboard value={value} index={1}><Traceability /></Dashboard>
                            </>
                        )}
                    </Grid>
                </Grid>
            )}
        </Box>
    );
};

export default VerticalTabs;
